import React, {useCallback, useEffect, useState} from 'react';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import FloatingMenuButton, {
    FloatingMenuButtonIcon,
} from 'components/customer/RoomPlanner/components/common/FloatingMenuButton';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useTheme} from 'styled-components';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';

const Copy = () => {
    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const {updateTotalProductCountManual} = useJobContext() as PartialJob;
    const {selectedCabinetGroup, setDuplicateHandler} = useRoomPlannerContext();
    const [isDuplicating, setIsDuplicating] = useState(false);
    const theme = useTheme();

    const handleCopy = useCallback(async () => {
        if (isDuplicating) return;

        const duplicate = (
            selectedCabinetGroup?.userData as {
                duplicateCabinet?: () => Promise<void> | void;
            }
        )?.duplicateCabinet;

        if (typeof duplicate === 'function') {
            setIsDuplicating(true);
            try {
                await Promise.resolve(duplicate());
                updateTotalProductCountManual(false, true, 1);
            } finally {
                setIsDuplicating(false);
            }
        }
    }, [isDuplicating, selectedCabinetGroup, updateTotalProductCountManual]);

    const handleCopyClick = useCallback(() => {
        void handleCopy();
    }, [handleCopy]);

    // Register the duplicate handler with the context for keyboard shortcut
    useEffect(() => {
        if (setDuplicateHandler) {
            setDuplicateHandler(handleCopyClick);
        }
        return () => setDuplicateHandler?.(null);
    }, [handleCopyClick, setDuplicateHandler]);

    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <strong>
                    {isDuplicating ? 'Duplicating...' : 'Duplicate (D)'}
                </strong>
            }>
            <FloatingMenuButton
                onClick={handleCopyClick}
                disabled={isDuplicating}
                aria-busy={isDuplicating}
                aria-label={
                    isDuplicating ? 'Duplicating cabinet' : 'Duplicate cabinet'
                }>
                <FloatingMenuButtonIcon
                    className="icon_button"
                    color={
                        isMobileLandscape || isPortraitDevices
                            ? theme.colors.primaryIconColor.main
                            : '#b2bec3'
                    }
                    iconName="Options-Copy.svg"
                />
                <label>Copy</label>
            </FloatingMenuButton>
        </OverlayTrigger>
    );
};

export default Copy;
